import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { SettingsProvider } from './contexts/SettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Homepage from './pages/Homepage';
import Dashboard from './pages/Dashboard';
import RetailPOS from './pages/RetailPOS';
import ProformaInvoice from './pages/ProformaInvoice';
import BulkSales from './pages/BulkSales';
import CreditNote from './pages/CreditNote';
import ProductList from './pages/ProductList';
import Categories from './pages/Categories';
import StockManagement from './pages/StockManagement';
import CustomerList from './pages/CustomerList';
import CustomerPayments from './pages/CustomerPayments';
import SupplierList from './pages/SupplierList';
import SupplierPayments from './pages/SupplierPayments';
import PurchaseManagement from './pages/PurchaseManagement';
import CreatePurchaseOrder from './pages/CreatePurchaseOrder';
import ApprovePurchases from './pages/ApprovePurchases';
import ReturnPurchases from './pages/ReturnPurchases';
import ReceivePurchases from './pages/ReceivePurchases';
import Expenses from './pages/Expenses';
import CashAnalysis from './pages/CashAnalysis';
import SalesReport from './pages/SalesReport';
import InventoryReport from './pages/InventoryReport';
import FinancialReport from './pages/FinancialReport';
import GeneralSettings from './pages/GeneralSettings';
import UserManagement from './pages/UserManagement';
import RolePermissionManagement from './pages/RolePermissionManagement';
import BackupRestore from './pages/BackupRestore';
import DataManagement from './pages/DataManagement';
import SystemLogs from './pages/SystemLogs';
import './App.css';


// Main App Component with routing logic
const AppContent = () => {
  const { isAuthenticated, shouldNavigate, isInitialized } = useAuth();

  // Don't render routes until auth is initialized
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            isAuthenticated && shouldNavigate ? 
            <Navigate to="/dashboard" replace /> : 
            <Homepage />
          } 
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {/* Reports Routes - moved right after dashboard */}
        <Route
          path="/sales-report"
          element={
            <ProtectedRoute>
              <SalesReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory-report"
          element={
            <ProtectedRoute>
              <InventoryReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/financial-report"
          element={
            <ProtectedRoute>
              <FinancialReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/retail-pos"
          element={
            <ProtectedRoute>
              <RetailPOS />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/proforma-invoice"
          element={
            <ProtectedRoute>
              <ProformaInvoice />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/bulk-sales"
          element={
            <ProtectedRoute>
              <BulkSales />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/credit-note"
          element={
            <ProtectedRoute>
              <CreditNote />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/products" 
          element={
            <ProtectedRoute>
              <ProductList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/categories" 
          element={
            <ProtectedRoute>
              <Categories />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/stock" 
          element={
            <ProtectedRoute>
              <StockManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/customers" 
          element={
            <ProtectedRoute>
              <CustomerList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/customer-payments" 
          element={
            <ProtectedRoute>
              <CustomerPayments />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/suppliers" 
          element={
            <ProtectedRoute>
              <SupplierList />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/supplier-payments"
          element={
            <ProtectedRoute>
              <SupplierPayments />
            </ProtectedRoute>
          }
        />
        {/* Purchase Routes */}
        <Route
          path="/purchases"
          element={
            <ProtectedRoute>
              <PurchaseManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/create"
          element={
            <ProtectedRoute>
              <CreatePurchaseOrder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/approve"
          element={
            <ProtectedRoute>
              <ApprovePurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/return"
          element={
            <ProtectedRoute>
              <ReturnPurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchases/receive"
          element={
            <ProtectedRoute>
              <ReceivePurchases />
            </ProtectedRoute>
          }
        />
        {/* Accounts Routes */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <Expenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-analysis"
          element={
            <ProtectedRoute>
              <CashAnalysis />
            </ProtectedRoute>
          }
        />
        {/* Settings Routes */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <GeneralSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/role-permissions"
          element={
            <ProtectedRoute>
              <RolePermissionManagement />
            </ProtectedRoute>
          }
        />
        {/* System Management Routes */}
        <Route
          path="/system/backup"
          element={
            <ProtectedRoute>
              <BackupRestore />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system/data"
          element={
            <ProtectedRoute>
              <DataManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system/logs"
          element={
            <ProtectedRoute>
              <SystemLogs />
            </ProtectedRoute>
          }
        />
        {/* Add more routes here as needed */}
        <Route path="*" element={
          isAuthenticated ? 
          <Navigate to="/dashboard" replace /> : 
          <Navigate to="/" replace />
        } />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <PermissionProvider>
            <div className="App">
              <AppContent />
              <Toaster
                position="top-right"
                reverseOrder={false}
                gutter={8}
                containerStyle={{
                  top: 20,
                  right: 20,
                }}
                toastOptions={{
                  // Global options that apply to all toasts
                  duration: 2500,
                  className: '',
                  style: {
                    background: '#363636',
                    color: '#fff',
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    maxWidth: '400px',
                    fontSize: '14px',
                  },
                  // Success toasts
                  success: {
                    duration: 2000,
                    style: {
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                    },
                    iconTheme: {
                      primary: '#fff',
                      secondary: '#10b981',
                    },
                  },
                  // Error toasts  
                  error: {
                    duration: 3000,
                    style: {
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: '#fff',
                    },
                    iconTheme: {
                      primary: '#fff',
                      secondary: '#ef4444',
                    },
                  },
                  // Loading toasts
                  loading: {
                    duration: Infinity, // Loading toasts should not auto-dismiss
                    style: {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                    },
                    iconTheme: {
                      primary: '#fff',
                      secondary: '#667eea',
                    },
                  },
                  // Blank/custom toasts
                  blank: {
                    duration: 4000,
                  },
                }}
              />
            </div>
          </PermissionProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
